/* Wikipedia & Wikidata workroom (#/client/{slug}/wiki) — a faithful port of the
 * owner-approved mockup design/mockups/2026-07-16-client-hub/wikipedia-hub.html.
 *
 * The mockup is fixture-heavy; per Package 3's rules the STRUCTURE is ported but
 * its numbers are NEVER copied. The real-vs-honest split:
 *   • Measured article health — the Wikimedia connector (feat/wikipedia-connector,
 *     FAILED audit, NOT merged) is unwired → quality class / protection / revisions
 *     / pageviews render an honest "connector not wired" state. Editions count,
 *     article tone and branded-search rank come from the RUN PAYLOAD and render as
 *     real facts with provenance.
 *   • Modeled-suggestion leverage banner — shown ONLY when derivable from the real
 *     AIQ payload (a wikipedia domain in citedDomains with an engine count);
 *     otherwise the slot is honestly absent (no invented "6/8").
 *   • PUBLISHED IS NOT VERIFIED bar — static rule copy, always shown (our probe
 *     semantics).
 *   • Content / Citations / Edits & Actors — connector + annotation backends are
 *     unbuilt → honest empty states describing what appears; NO fake article text,
 *     citations, or edits.
 *   • Talk & Requests — live cited expert outputs with an AM-triggered draft wake
 *     and single-step lifecycle controls through published. Verified remains
 *     probe-earned; the lifecycle rail and WP:PAID disclosure stay verbatim.
 *   • Wikidata — REAL: the payload's wikidata worksheet (current vs researched
 *     claims + receipts) + claim proposals + a QuickStatements proposal artifact
 *     (reuses ui/exports.ts). No Approve/Revise/Create-task buttons (nothing real
 *     to wire); facts + the real QuickStatements download only.
 *   • Languages — REAL: per-edition rows (tone, branded rank per market) from the
 *     payload; unassessed editions render the honest "not assessed" pattern.
 *
 * Lit conventions per views/wiki.ts. Tokens only (wikiWorkroom.css).
 */
import { html, nothing, type TemplateResult } from 'lit';
import { ApiError } from '../../api/client.ts';
import {
  getOutputs,
  patchOutput,
  postRecheck,
  postWake,
  verifyOutput,
  type DraftLifecycle,
  type DraftLifecycleTransition,
  type ExpertCitation,
  type ExpertOutput,
  type ProbeVerdict,
  type Wake,
} from '../../api/experts.ts';
import type { AppPayloadState } from '../../derive/state-shape.ts';
import type { NormalizedAsset, WikidataWorksheetRow } from '../../types/contract.ts';
import { exportQuickStatements } from '../../ui/exports.ts';
import { surfaceContextBar } from './surfaceChrome.ts';
import {
  ensureWikipedia,
  primaryWikiArticleUrl,
  resetWikiConnector,
  type ConnectorState,
} from './wikiConnector.ts';
import {
  connectorCitationsPanel,
  connectorContentPanel,
  connectorEditsPanel,
  connectorHealthBlock,
} from './wikiConnectorPanels.ts';

/* Six artifact tabs; Content is the mockup default. */
type ArtifactTab = 'content' | 'cites' | 'edits' | 'talk' | 'data' | 'langs';
let artifactTab: ArtifactTab = 'content';
/** FIX-audit F12c: the slug the current tab selection belongs to. A cockpit→
 * cockpit switch (client A → B) does NOT run teardownCockpit, so without keying
 * the tab by slug, client A's selected artifact tab carried into client B. */
let artifactSlug: string | null = null;

type ExpertLoadPhase = 'loading' | 'ready' | 'error';
type ExpertAction = 'idle' | 'wake' | 'patch' | 'verify';

export interface ExpertViewState {
  phase: ExpertLoadPhase;
  outputs: ExpertOutput[];
  action: ExpertAction;
  notice: string | null;
  fallbackNotWired: boolean;
  message?: string;
}

interface ExpertCacheEntry extends ExpertViewState {
  fetchedAt: number;
  inFlight: boolean;
  requestSeq: number;
  instruction: string;
}

interface TalkActions {
  instruction: string;
  onInstruction: (instruction: string) => void;
  onWake: () => void;
  onAdvance: (output: ExpertOutput, nextState: DraftLifecycleTransition) => void;
  onVerify: (output: ExpertOutput) => void;
}

const OUTPUT_READY_TTL_MS = 60_000;
const expertCache = new Map<string, ExpertCacheEntry>();

function newExpertEntry(): ExpertCacheEntry {
  return {
    phase: 'loading',
    outputs: [],
    action: 'idle',
    notice: null,
    fallbackNotWired: false,
    fetchedAt: Date.now(),
    inFlight: false,
    requestSeq: 0,
    instruction: '',
  };
}

function expertView(entry: ExpertCacheEntry): ExpertViewState {
  return {
    phase: entry.phase,
    outputs: entry.outputs,
    action: entry.action,
    notice: entry.notice,
    fallbackNotWired: entry.fallbackNotWired,
    message: entry.message,
  };
}

function outputCacheIsStale(entry: ExpertCacheEntry, now: number): boolean {
  if (entry.phase === 'ready') return now - entry.fetchedAt > OUTPUT_READY_TTL_MS;
  if (entry.phase === 'error') return true;
  return false;
}

async function loadExpertOutputs(
  slug: string,
  rerender: () => void,
  force = false,
): Promise<ExpertOutput[] | null> {
  let entry = expertCache.get(slug);
  if (!entry) {
    entry = newExpertEntry();
    expertCache.set(slug, entry);
  }
  if (entry.inFlight && !force) return entry.phase === 'ready' ? entry.outputs : null;

  const requestSeq = entry.requestSeq + 1;
  entry.requestSeq = requestSeq;
  entry.inFlight = true;
  try {
    const { outputs } = await getOutputs(slug, 'wikipedia');
    if (expertCache.get(slug) !== entry || entry.requestSeq !== requestSeq) return outputs;
    entry.phase = 'ready';
    entry.outputs = outputs;
    entry.fetchedAt = Date.now();
    entry.inFlight = false;
    entry.message = undefined;
    rerender();
    return outputs;
  } catch (error: unknown) {
    if (expertCache.get(slug) !== entry || entry.requestSeq !== requestSeq) return null;
    entry.phase = 'error';
    entry.outputs = [];
    entry.message = error instanceof Error ? error.message : String(error);
    // Match signalsStrip.ts: render the error once while still marked in-flight,
    // then make a later render eligible to retry without triggering a loop now.
    entry.inFlight = true;
    rerender();
    if (expertCache.get(slug) === entry && entry.requestSeq === requestSeq) entry.inFlight = false;
    return null;
  }
}

function ensureExpertOutputs(slug: string, rerender: () => void): ExpertCacheEntry {
  let entry = expertCache.get(slug);
  if (!entry) {
    entry = newExpertEntry();
    expertCache.set(slug, entry);
    void loadExpertOutputs(slug, rerender);
  } else if (outputCacheIsStale(entry, Date.now()) && !entry.inFlight) {
    void loadExpertOutputs(slug, rerender);
  }
  return entry;
}

/** Reset the workroom tab + connector state (called on cockpit teardown). */
export function resetWikiWorkroom(): void {
  artifactTab = 'content';
  artifactSlug = null;
  expertCache.clear();
  resetWikiConnector();
}

interface WikiDeps {
  state: AppPayloadState;
  brand: string;
  slug: string;
  payloadLoaded: boolean;
  rerender: () => void;
}

/* ── derivations (all real, from the loaded run payload) ───────────────────── */
function wikiAssets(state: AppPayloadState): NormalizedAsset[] {
  return state.assets.filter((a) => a.platform === 'Wikipedia' || a.assetType === 'wiki');
}
function brandedWikiHit(state: AppPayloadState): { rank: number; page: number; rating: string | null; url: string } | null {
  const caps = [state.serp, ...(state.serps || [])].filter(Boolean) as { results?: { rank: number; page: number; rating: string | null; url: string }[] }[];
  for (const c of caps) {
    const hit = (c.results || []).find((r) => /wikipedia\.org/.test(r.url) && r.page === 1);
    if (hit) return { rank: hit.rank, page: hit.page, rating: hit.rating, url: hit.url };
  }
  return null;
}
/** Full hostname (incl. language subdomain, e.g. `en.wikipedia.org`) — the
 * identity that distinguishes one Wikipedia edition from another. */
function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try { return new URL(url).hostname.toLowerCase(); } catch { return null; }
}
/** FIX-audit F12a: the page-one branded-SERP rank for a SPECIFIC edition —
 * matched by that edition's own hostname, never the one global "first wikipedia
 * result" applied to every edition. Null ⇒ this edition wasn't observed on any
 * tracked page one → "not measured", never a borrowed rank. */
function editionRank(state: AppPayloadState, a: NormalizedAsset): { rank: number; page: number } | null {
  const eh = hostOf(a.url);
  if (!eh) return null;
  const caps = [state.serp, ...(state.serps || [])].filter(Boolean) as { results?: { rank: number; page: number; url: string }[] }[];
  for (const c of caps) {
    const hit = (c.results || []).find((r) => r.page === 1 && hostOf(r.url) === eh);
    if (hit) return { rank: hit.rank, page: hit.page };
  }
  return null;
}
/** N of M AI engines citing the wikipedia article domain, or null (honest absence). */
function wikiLeverage(state: AppPayloadState): { n: number; m: number } | null {
  const doms = state.aiNarrative?.citedDomains || [];
  const wikiDom = doms.find((d) => /wikipedia\.org/i.test(d.domain));
  const engines = (state.aiNarrative?.engines || []).length;
  if (!wikiDom || typeof wikiDom.models !== 'number' || wikiDom.models <= 0) return null;
  // FIX-audit F12b: "N of M engines" needs a REAL engine denominator. When the
  // run recorded no engine roster, do NOT fabricate m = n (which reads "2 of 2");
  // render nothing instead.
  if (!engines || engines <= 0) return null;
  return { n: wikiDom.models, m: engines };
}
const STATE_CHIP: Record<string, string> = { confirmed: 'green', missing: 'amber', stale: 'neg' };
const DRAFT_STATES: DraftLifecycle[] = ['draft', 'submitted', 'responded', 'published', 'verified'];
const OVER_BUDGET_CONFIRM = "this exceeds the client's ambient budget — run anyway?";

export function nextDraftLifecycle(
  state: DraftLifecycle | null,
): DraftLifecycleTransition | null {
  if (state === 'draft') return 'submitted';
  if (state === 'submitted') return 'responded';
  if (state === 'responded') return 'published';
  return null;
}

export function shouldRetryOverBudget(
  error: unknown,
  confirmRun: (message: string) => boolean,
): boolean {
  return error instanceof ApiError && error.status === 409 && confirmRun(OVER_BUDGET_CONFIRM);
}

function bodyString(output: ExpertOutput, field: string): string {
  const value = output.body[field];
  return typeof value === 'string' ? value : '';
}

function citationHref(citation: ExpertCitation): string | null {
  if (typeof citation.url !== 'string') return null;
  try {
    const url = new URL(citation.url);
    return url.protocol === 'http:' || url.protocol === 'https:' ? citation.url : null;
  } catch {
    return null;
  }
}

function citationLabel(citation: ExpertCitation, index: number): string {
  if (typeof citation.source === 'string' && citation.source.trim()) return citation.source;
  const href = citationHref(citation);
  if (href) {
    try { return new URL(href).hostname; } catch { /* already guarded; fall through */ }
  }
  return `Source ${index + 1}`;
}

function citationChips(citations: ExpertCitation[]): TemplateResult {
  return html`<div class="ww-citations">${citations.map((citation, index) => {
    const href = citationHref(citation);
    const label = citationLabel(citation, index);
    return href
      ? html`<a class="ww-citation" href=${href} target="_blank" rel="noopener">${label} ↗</a>`
      : html`<span class="ww-citation">${label}</span>`;
  })}</div>`;
}

function activeOutputs(outputs: ExpertOutput[], kind: ExpertOutput['kind']): ExpertOutput[] {
  return outputs.filter((output) => output.kind === kind && output.status === 'active');
}

export function renderWikipediaAdvisories(outputs: ExpertOutput[]): TemplateResult {
  const advisories = activeOutputs(outputs, 'advisory');
  return html`<div class="ww-expert-advisories">${advisories.map((output) => html`
    <article class="ww-expert-advisory">
      <span class="ww-eyebrow">Modeled suggestion · advisory</span>
      <p>${bodyString(output, 'text')}</p>
      ${citationChips(output.citations)}
    </article>
  `)}</div>`;
}

export function renderWikipediaFindings(outputs: ExpertOutput[]): TemplateResult {
  const findings = activeOutputs(outputs, 'finding');
  return html`<aside class="ww-card ww-expert-findings">
    <div class="ww-card-head">
      <h2 class="ww-h2">Expert findings</h2>
      <span class="ww-source">Wikipedia expert · cited</span>
    </div>
    ${findings.map((output) => html`<article class="ww-expert-finding">
      <span class="ww-source">Wikipedia expert · cited</span>
      <h3 class="ww-h3">${bodyString(output, 'title')}</h3>
      <p>${bodyString(output, 'detail')}</p>
      ${citationChips(output.citations)}
    </article>`)}
  </aside>`;
}

/* ── measured article health (live connector metrics + real payload facts) ── */
function healthCard(
  state: AppPayloadState,
  conn: ConnectorState,
  outputs: ExpertOutput[],
): TemplateResult {
  const wikis = wikiAssets(state);
  const hit = brandedWikiHit(state);
  const toneSet = new Set(wikis.map((a) => a.sentiment).filter(Boolean));
  const tone = toneSet.size ? [...toneSet].join(' / ') : null;
  return html`<section class="ww-health">
    <div class="ww-health-l">
      <span class="ww-eyebrow">Measured article health · Wikipedia</span>
      ${connectorHealthBlock(conn)}
      <div class="ww-payloadfacts">
        <span class="ww-eyebrow">From this research run</span>
        <div class="ww-realfacts">
          <div class="ww-stat"><span>Language editions</span><strong>${wikis.length || '—'}</strong><small>${wikis.length ? 'mapped in this run' : 'none mapped'}</small></div>
          <div class="ww-stat"><span>Branded rank</span><strong>${hit ? `#${hit.rank}` : '—'}</strong><small>${hit ? `page ${hit.page}${hit.rating === 'negative' ? ' · Undesirable' : ''} · IMPACT` : 'not on branded page one'}</small></div>
          <div class="ww-stat"><span>Article tone</span><strong>${tone || '—'}</strong><small>${tone ? 'research judgment' : 'not assessed this run'}</small></div>
        </div>
      </div>
    </div>
    <div class="ww-health-r">${leverageBanner(state, outputs)}</div>
  </section>`;
}

/* ── modeled-suggestion leverage banner (only if derivable) ────────────────── */
// Deliberate precedence: real expert advisories outrank the derived leverage
// banner — when any advisory is active, it replaces the banner rather than
// appending alongside it, so the AM never sees the modeled guess and the
// expert's actual read in the same slot.
function leverageBanner(state: AppPayloadState, outputs: ExpertOutput[]): TemplateResult {
  if (activeOutputs(outputs, 'advisory').length) {
    return html`<div class="ww-leverage">${renderWikipediaAdvisories(outputs)}</div>`;
  }
  const lev = wikiLeverage(state);
  if (!lev) {
    return html`<div class="ww-leverage ww-leverage-absent">
      <span class="ww-eyebrow">Modeled suggestion · advisory</span>
      <p class="hub-muted">Per-engine wiki-citation leverage isn’t derivable from this run’s AIQ payload —
        no invented ratio is shown. It appears when the AI plane maps engine-level citations to the article domain.</p>
    </div>`;
  }
  return html`<div class="ww-leverage">
    <span class="ww-leverage-mark">${lev.n}/${lev.m}</span>
    <span class="ww-eyebrow">Modeled suggestion · advisory, human decides</span>
    <b>${lev.n} of ${lev.m} AI engines cite this article’s domain — a lede fix is a high-leverage ORM move.</b>
    <p class="hub-muted">Measured citation counts, modeled leverage — not a measured outcome, and never permission to edit.</p>
  </div>`;
}

/* ── PUBLISHED IS NOT VERIFIED invariant bar (static rule copy) ────────────── */
const invariantBar: TemplateResult = html`<div class="ww-invariant">
  <b>PUBLISHED IS NOT VERIFIED</b>
  <span>Only a deterministic recheck probe observing the exact live change earns verification. Blocked or
    unknown data remains unverifiable — never a fake value or a failure.</span>
  <span class="ww-source">published → probe → observed → verified</span>
</div>`;

/* ── artifact tab strip ────────────────────────────────────────────────────── */
const TABS: Array<{ id: ArtifactTab; label: string }> = [
  { id: 'content', label: '1 · Content' },
  { id: 'cites', label: '2 · Citations' },
  { id: 'edits', label: '3 · Edits & Actors' },
  { id: 'talk', label: '4 · Talk & Requests' },
  { id: 'data', label: '5 · Wikidata' },
  { id: 'langs', label: '6 · Languages' },
];
function tabStrip(rerender: () => void): TemplateResult {
  return html`<div class="ww-tabbar" role="tablist" aria-label="artifacts">
    ${TABS.map((t) => html`<button class=${`ww-tab ${artifactTab === t.id ? 'on' : ''}`} role="tab"
      aria-selected=${artifactTab === t.id ? 'true' : 'false'}
      @click=${() => { artifactTab = t.id; rerender(); }}>${t.label}</button>`)}
  </div>`;
}

/* ── honest empty helper ───────────────────────────────────────────────────── */
function honestEmpty(eyebrow: string, title: string, body: string): TemplateResult {
  return html`<section class="ww-panel"><div class="ww-card">
    <span class="ww-eyebrow">${eyebrow}</span>
    <h2 class="ww-h2">${title}</h2>
    <p class="ww-empty">${body}</p>
  </div></section>`;
}

/* ── panel: Wikidata (REAL) ────────────────────────────────────────────────── */
function wikidataPanel(state: AppPayloadState, rerender: () => void): TemplateResult {
  const wd = state.wikidata;
  const rows = wd?.worksheet || [];
  if (!rows.length && !wd?.present) {
    return honestEmpty('Wikidata worksheet', 'No Wikidata worksheet this run',
      'The structured-claims worksheet (current vs researched values, receipts) appears once the entity plane resolves a Wikidata item for this client.');
  }
  const chipFor = (r: WikidataWorksheetRow): TemplateResult => {
    const cls = STATE_CHIP[r.state] || '';
    return html`<span class=${`ww-chip ${cls}`}>${r.state}</span>`;
  };
  const proposals = rows.filter((r) => r.state === 'missing' || r.state === 'stale');
  const canExport = proposals.length > 0;
  return html`<section class="ww-panel"><div class="ww-stack">
    <div class="ww-card">
      <div class="ww-card-head"><h2 class="ww-h2">Wikidata worksheet${wd?.qid ? ` · ${wd.qid}` : ''}</h2><span class="ww-source">payload worksheet + receipts</span></div>
      <div class="ww-tablewrap"><table class="ww-table">
        <thead><tr><th>Property</th><th>Current</th><th>Researched</th><th>Source</th><th>State</th></tr></thead>
        <tbody>${rows.map((r) => html`<tr>
          <td><b>${r.property}</b><small>${r.pcode}</small></td>
          <td>${r.current || html`<span class="hub-muted">—</span>`}</td>
          <td class=${r.research ? '' : 'ww-blocked'}>${r.research || 'unverifiable'}</td>
          <td class="ww-mono">${r.source || '—'}</td>
          <td>${chipFor(r)}</td>
        </tr>`)}</tbody>
      </table></div>
    </div>

    ${proposals.length ? html`<div class="ww-card">
      <div class="ww-card-head"><h2 class="ww-h2">Claim proposals</h2><span class="ww-chip violet">Approval ≠ apply</span></div>
      <div class="ww-claims">${proposals.map((r) => html`<div class="ww-claim">
        <h3>${r.state === 'missing' ? 'Add' : 'Replace'} ${r.property} · ${r.pcode}</h3>
        <div class="ww-claim-val"><small>Proposed (researched)</small><strong>${r.research || 'unverifiable'}</strong></div>
        <div class="ww-receipt">${r.source ? `receipt ${r.source}` : 'no receipt on record'}</div>
      </div>`)}</div>
      <p class="ww-note hub-muted">Facts only — no Approve/Apply here. A claim moves only after a person publishes it and a
        recheck probe observes the exact live value.</p>
    </div>` : nothing}

    <div class="ww-card">
      <div class="ww-card-head"><h2 class="ww-h2">QuickStatements proposal artifact</h2><span class="ww-chip violet">Never auto-applied</span></div>
      ${canExport
        ? html`<p class="ww-note">A QuickStatements batch for the researched values is generated on demand — a proposal you
            paste after human review, never applied from here.</p>
          <button class="ww-btn" @click=${() => { exportQuickStatements(state); rerender(); }}>Download QuickStatements ↓</button>`
        : html`<p class="ww-empty">No proposable claims this run — nothing to export.</p>`}
      <div class="ww-proposal-only"><strong>PROPOSAL ONLY</strong> — after human publication, an exact-value-and-qualifier
        probe must observe the live claim before verification.</div>
    </div>
  </div></section>`;
}

/* ── panel: Languages (REAL) ───────────────────────────────────────────────── */
function languagesPanel(state: AppPayloadState): TemplateResult {
  const wikis = wikiAssets(state);
  if (!wikis.length) {
    return honestEmpty('Language editions & markets', 'No language editions mapped this run',
      'Per-edition tone, divergence and branded-search rank appear once Wikipedia editions are mapped for this client.');
  }
  const rows = wikis.map((a) => {
    // FIX-audit F12a: per-EDITION rank, matched by this edition's own hostname —
    // not the single global "first wikipedia hit" stamped onto whichever edition
    // happened to be first.
    const hit = editionRank(state, a);
    const isRollup = /other/i.test(a.id) || /other/i.test(a.title);
    const lang = isRollup ? 'Other editions'
      : (a.title.match(/\(([^)]+)\)/) || [])[1] || (a.url && (a.url.match(/\/\/(\w+)\.wikipedia/) || [])[1]) || a.title;
    const verdict = isRollup ? 'Not assessed'
      : a.sentiment === 'negative' ? 'High divergence' : a.sentiment === 'mixed' ? 'Medium divergence'
        : a.sentiment ? 'Low divergence' : 'Unverifiable';
    const sub = isRollup ? 'existence confirmed; parity is a coverage gap'
      : a.sentiment ? (a.notes || '').slice(0, 110) || 'tone assessed in research' : 'article tone not assessed this run';
    return html`<div class=${`ww-langrow ${a.sentiment === 'negative' ? 'risk' : ''}`}>
      <b class="ww-lang">${lang}</b>
      <div class="ww-lang-div"><b>${verdict}</b><small>${sub}</small></div>
      <span class="ww-chip">${a.region || (isRollup ? 'Markets TBD' : '—')}</span>
      <span class="ww-lang-rank">${hit ? `Rank #${hit.rank}` : (isRollup ? '' : 'not measured')}</span>
    </div>`;
  });
  return html`<section class="ww-panel"><div class="ww-card">
    <div class="ww-card-head"><h2 class="ww-h2">Language editions &amp; markets</h2><span class="ww-source">sitelinks + IMPACT SERPs</span></div>
    ${rows}
  </div></section>`;
}

/* ── panel: Talk & Requests (live drafting + static rule copy) ─────────────── */
export function renderTalkPanel(view: ExpertViewState, actions: TalkActions): TemplateResult {
  const drafts = activeOutputs(view.outputs, 'draft');
  const busy = view.action !== 'idle';
  return html`<section class="ww-panel"><div class="ww-draft-grid">
    <article class="ww-card">
      <span class="ww-eyebrow">AI drafting workspace</span>
      ${view.fallbackNotWired
        ? html`<h2 class="ww-h2">Drafting service not wired</h2>
          <p class="ww-empty">The AI drafting workspace — agent-prepared, policy-aware COI request drafts with proposed
            language, the WP:NPOV/UNDUE/V/RS/BLP policy case, and checked supporting sources — appears when the drafting
            service lands. No draft text is fabricated here.</p>`
        : html`<div class="ww-draft-action">
            <label for="ww-draft-instruction">Drafting instruction</label>
            <textarea id="ww-draft-instruction" rows="3" .value=${actions.instruction}
              ?disabled=${busy}
              placeholder="What should the COI request address?"
              @input=${(event: Event) => actions.onInstruction(
                (event.currentTarget as HTMLTextAreaElement).value,
              )}></textarea>
            <button class="ww-btn ww-wake-btn" ?disabled=${busy || !actions.instruction.trim()}
              @click=${actions.onWake}>Draft COI request</button>
          </div>
          ${view.action === 'wake'
            ? html`<div class="ww-action-progress" role="status">Drafting Wikipedia request…</div>`
            : nothing}
          ${view.action === 'patch'
            ? html`<div class="ww-action-progress" role="status">Updating request lifecycle…</div>`
            : nothing}
          ${view.action === 'verify'
            ? html`<div class="ww-action-progress" role="status">Running verification probe…</div>`
            : nothing}
          ${view.notice ? html`<div class="ww-action-notice" role="status">${view.notice}</div>` : nothing}
          ${view.phase === 'loading' && view.action !== 'wake'
            ? html`<p class="ww-empty">Loading Wikipedia expert outputs…</p>`
            : nothing}
          ${view.phase === 'error'
            ? html`<p class="ww-empty">Drafting outputs unavailable — ${view.message || 'request failed'}. Nothing is fabricated here.</p>`
            : nothing}
          ${view.phase === 'ready' && view.action !== 'wake' && drafts.length === 0
            ? html`<p class="ww-empty">No drafts yet — ask the expert for one, or wait for an article signal.</p>`
            : nothing}
          ${drafts.map((output) => {
            const lifecycle = output.lifecycle_state;
            const next = nextDraftLifecycle(lifecycle);
            const currentIndex = lifecycle ? DRAFT_STATES.indexOf(lifecycle) : -1;
            return html`<section class="ww-draft-item">
              <div class="ww-card-head">
                <h3 class="ww-h3">${bodyString(output, 'title')}</h3>
                <span class="ww-source">Wikipedia expert · cited</span>
              </div>
              <pre>${bodyString(output, 'body')}</pre>
              ${citationChips(output.citations)}
              <div class="ww-draft-lifecycle" aria-label="request lifecycle">
                ${DRAFT_STATES.map((state, index) => html`<span class=${
                  index === currentIndex ? 'now' : index < currentIndex ? 'done' : ''
                } aria-current=${index === currentIndex ? 'step' : nothing}>${state}</span>`)}
              </div>
              ${next
                ? html`<button class="ww-btn ww-next-btn" ?disabled=${busy}
                    @click=${() => actions.onAdvance(output, next)}>Mark ${next}</button>`
                : nothing}
              ${lifecycle === 'published'
                ? html`<button class="ww-btn ww-verify-btn" ?disabled=${busy}
                    @click=${() => actions.onVerify(output)}>Verify via probe</button>`
                : nothing}
            </section>`;
          })}`}
      <div class="ww-paid">
        <b>MANDATORY WP:PAID DISCLOSURE</b>
        <span>Five Blocks is a paid editor acting on behalf of the client. The requester must disclose the employer,
          client, and relevant affiliation on the user page and with the Talk-page request, as required by Wikimedia’s
          Terms of Use and WP:PAID. This disclosure cannot be removed from the submission.</span>
      </div>
    </article>
    <aside class="ww-stack">
      <div class="ww-card ww-gate">
        <span class="ww-eyebrow">Lifecycle</span>
        <h3 class="ww-h3">Every request is human-gated</h3>
        <div class="ww-lifecycle"><span class="now">draft</span><span>submitted</span><span>responded</span><span>published</span><span>verified</span></div>
        <div class="ww-verify-rule"><b>VERIFIED is earned only when a deterministic recheck probe observes the exact text
          in the live article.</b> Publishing, approval, or a click never verifies a request.</div>
        <p class="hub-muted">The agent proposes; a person reviews the language, evidence, policy case and disclosure, then
          chooses whether to submit. Nothing is auto-submitted to Wikipedia.</p>
      </div>
    </aside>
  </div></section>`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function mergePatchedOutput(
  preActionOutputs: ExpertOutput[],
  currentOutputs: ExpertOutput[],
  patchedOutput: ExpertOutput,
): ExpertOutput[] {
  const merged = new Map<number, ExpertOutput>();
  for (const output of preActionOutputs) merged.set(output.id, output);
  for (const output of currentOutputs) merged.set(output.id, output);
  merged.set(patchedOutput.id, patchedOutput);
  return [...merged.values()];
}

async function wakeWikipediaExpert(slug: string, rerender: () => void): Promise<void> {
  const entry = expertCache.get(slug);
  if (!entry || entry.action !== 'idle' || !entry.instruction.trim()) return;
  const priorIds = new Set(entry.outputs.map((output) => output.id));
  entry.action = 'wake';
  entry.notice = null;
  entry.fallbackNotWired = false;
  rerender();
  try {
    let wakeStatus: Wake['status'];
    try {
      wakeStatus = (await postWake(slug, 'wikipedia', entry.instruction)).wake.status;
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 409) {
        if (!shouldRetryOverBudget(error, (message) => window.confirm(message))) {
          entry.notice = 'Request held by the ambient budget — no expert wake was run.';
          return;
        }
        wakeStatus = (
          await postWake(slug, 'wikipedia', entry.instruction, true)
        ).wake.status;
      } else {
        throw error;
      }
    }
    const refreshed = await loadExpertOutputs(slug, rerender, true);
    if (wakeStatus === 'error') {
      entry.notice = refreshed === null
        ? 'Wikipedia expert wake failed, and outputs could not be refreshed.'
        : 'Wikipedia expert wake failed — no completion was recorded.';
    } else if (refreshed === null) {
      entry.notice = 'Expert wake completed, but outputs could not be refreshed.';
    } else if (!refreshed.some((output) => !priorIds.has(output.id))) {
      entry.notice = 'Expert wake completed, but no new Wikipedia output was produced.';
    } else {
      entry.instruction = '';
      entry.notice = 'Wikipedia expert output is ready.';
    }
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 404) {
      entry.fallbackNotWired = true;
      entry.notice = null;
    } else {
      entry.notice = `Draft request failed — ${errorMessage(error)}.`;
    }
  } finally {
    if (expertCache.get(slug) === entry) {
      entry.action = 'idle';
      rerender();
    }
  }
}

async function advanceDraft(
  slug: string,
  output: ExpertOutput,
  nextState: DraftLifecycleTransition,
  rerender: () => void,
): Promise<void> {
  const entry = expertCache.get(slug);
  if (!entry || entry.action !== 'idle' || nextDraftLifecycle(output.lifecycle_state) !== nextState) {
    return;
  }
  const preActionOutputs = [...entry.outputs];
  entry.action = 'patch';
  entry.notice = null;
  rerender();
  try {
    const { output: patchedOutput } = await patchOutput(
      slug,
      output.id,
      { lifecycleState: nextState },
    );
    const patchedOutputs = mergePatchedOutput(
      preActionOutputs,
      entry.outputs,
      patchedOutput,
    );
    entry.outputs = patchedOutputs;
    entry.phase = 'ready';
    entry.fetchedAt = Date.now();
    entry.message = undefined;
    rerender();
    const refreshed = await loadExpertOutputs(slug, rerender, true);
    if (refreshed === null && expertCache.get(slug) === entry) {
      entry.outputs = patchedOutputs;
      entry.phase = 'ready';
      entry.fetchedAt = Date.now();
      entry.message = undefined;
      entry.notice = `Lifecycle changed to ${nextState}, but fresh outputs could not be loaded.`;
    }
  } catch (error: unknown) {
    entry.notice = `Lifecycle update failed — ${errorMessage(error)}.`;
  } finally {
    if (expertCache.get(slug) === entry) {
      entry.action = 'idle';
      rerender();
    }
  }
}

/* Probe-evidenced verified (P2 Task 5): runs the SAME deterministic recheck
 * probe the task board uses against THIS DRAFT'S OWN article — its
 * `subject_key`, set by the expert handler to the exact article it targeted
 * (service/app/experts/wikipedia.py), never the client's general "primary"
 * Wikipedia article. Probing the wrong URL would let evidence for a
 * different article satisfy this draft's gate — the same false-verify class
 * ws_profiles.save_recheck's allow_verify already guards on the task board
 * (review 2026-07-20). Then — ONLY on an observed verdict — PATCHes
 * lifecycleState to "verified". A not-observed/unavailable verdict never
 * attempts the PATCH; it renders honest copy instead. The router's evidence
 * gate (routers/experts.py) remains the source of truth — this is a
 * convenience that can only ever ask for the SAME gate the router already
 * enforces.
 *
 * The probe call and the verify PATCH are labeled separately on failure: a
 * probe-transport failure reads "Verification probe failed", while a
 * failure writing the earned verified state (e.g. a 409 because the draft's
 * lifecycle moved underneath this request) reads "Draft verification write
 * failed" — the AM needs to know which side broke. */
async function verifyDraftViaProbe(
  slug: string,
  output: ExpertOutput,
  rerender: () => void,
): Promise<void> {
  const entry = expertCache.get(slug);
  if (!entry || entry.action !== 'idle' || output.lifecycle_state !== 'published') return;
  const target = output.subject_key;
  if (!target) {
    entry.notice = 'This draft has no article recorded — nothing to probe.';
    rerender();
    return;
  }
  const preActionOutputs = [...entry.outputs];
  entry.action = 'verify';
  entry.notice = null;
  rerender();
  try {
    let verdict: ProbeVerdict;
    try {
      ({ verdict } = await postRecheck(slug, 'article-content', target));
    } catch (error: unknown) {
      entry.notice = `Verification probe failed — ${errorMessage(error)}.`;
      return;
    }
    if (verdict !== 'observed') {
      entry.notice = 'Probe could not confirm the change is live — verified stays unearned.';
      return;
    }
    try {
      const { output: patchedOutput } = await verifyOutput(slug, output.id);
      const patchedOutputs = mergePatchedOutput(preActionOutputs, entry.outputs, patchedOutput);
      entry.outputs = patchedOutputs;
      entry.phase = 'ready';
      entry.fetchedAt = Date.now();
      entry.message = undefined;
      rerender();
      const refreshed = await loadExpertOutputs(slug, rerender, true);
      if (refreshed === null && expertCache.get(slug) === entry) {
        entry.outputs = patchedOutputs;
        entry.phase = 'ready';
        entry.fetchedAt = Date.now();
        entry.message = undefined;
        entry.notice = 'Verified, but fresh outputs could not be loaded.';
      } else {
        entry.notice = 'Probe observed the change — verified.';
      }
    } catch (error: unknown) {
      entry.notice = `Draft verification write failed — ${errorMessage(error)}.`;
    }
  } finally {
    if (expertCache.get(slug) === entry) {
      entry.action = 'idle';
      rerender();
    }
  }
}

function connectorEvidenceWithFindings(
  evidence: TemplateResult,
  outputs: ExpertOutput[],
): TemplateResult {
  if (!activeOutputs(outputs, 'finding').length) return evidence;
  return html`<div class="ww-evidence-grid"><div>${evidence}</div>${renderWikipediaFindings(outputs)}</div>`;
}

/* ── coverage & provenance footer (real) ───────────────────────────────────── */
function connectorProvenance(conn: ConnectorState): string {
  if (conn.status === 'ok' && conn.record) {
    const cov = conn.record.coverage.articles[0]?.subresources;
    const pv = cov?.pageviews;
    const parts = [`page info + revisions + citations + pageviews live (fetched ${conn.record.fetchedAt.slice(0, 16).replace('T', ' ')}Z)`];
    if (pv && pv.status === 'partial') parts.push(`pageviews span ${pv.daysFetched}/${pv.requestedDays}d (real, not padded)`);
    parts.push('Talk drafting: expert-backed · annotation backend: out of scope');
    return parts.join(' · ');
  }
  if (conn.status === 'loading') return 'Wikimedia connector: fetching live article facts…';
  if (conn.status === 'idle' || !conn.article) return 'Wikimedia connector: no Wikipedia article mapped for this client';
  return `Wikimedia connector: unavailable this fetch (${conn.reason || 'no facts returned'}) — nothing inferred`;
}
function coverageFooter(state: AppPayloadState, conn: ConnectorState): TemplateResult {
  const asOf = state.generatedAt ? state.generatedAt.slice(0, 10) : null;
  const receipts = [...new Set((state.wikidata?.worksheet || []).map((r) => r.source).filter(Boolean))] as string[];
  return html`<footer class="ww-coverage">
    <div>
      <b>Coverage &amp; provenance</b>
      <div>Wikidata: entity worksheet from this run${state.wikidata?.qid ? ` (${state.wikidata.qid})` : ''} · Wikipedia:
        editions + branded rank from research/IMPACT · ${connectorProvenance(conn)}.</div>
      <div>${asOf ? `Run generated ${asOf}` : 'Run date not stamped'} · blocked / partial data stays unverifiable — no inferred values substituted.</div>
    </div>
    <div class="ww-receipts"><b>Receipts</b>
      <div>${receipts.length ? receipts.slice(0, 6).join(' · ') : 'no worksheet receipts this run'}</div>
    </div>
  </footer>`;
}

/* ── compose ───────────────────────────────────────────────────────────────── */
export function renderWikiWorkroom(deps: WikiDeps): TemplateResult {
  const { state, brand, slug, payloadLoaded, rerender } = deps;
  // F12c: the artifact tab is per-client — reset it when the client changes so
  // a tab selected on client A never persists onto client B.
  if (slug !== artifactSlug) { artifactTab = 'content'; artifactSlug = slug; }
  if (!payloadLoaded) {
    return html`<div class="wikiwork">
      ${surfaceContextBar(brand, slug, 'wiki')}
      <div class="ww-content">
        <div class="ww-card"><p class="ww-empty">No research run for this client yet — the Wikipedia &amp; Wikidata
          workroom fills in once the first run lands.</p></div>
      </div>
    </div>`;
  }
  // Kick off (once per slug+article) the live Wikimedia facts fetch; the panel
  // renders loading/real/honest-unavailable from this snapshot.
  const conn = ensureWikipedia(slug, primaryWikiArticleUrl(state), rerender);
  const expertEntry = ensureExpertOutputs(slug, rerender);
  const outputs = expertEntry.outputs;
  let panel: TemplateResult;
  switch (artifactTab) {
    case 'data': panel = wikidataPanel(state, rerender); break;
    case 'langs': panel = languagesPanel(state); break;
    case 'talk': panel = renderTalkPanel(expertView(expertEntry), {
      instruction: expertEntry.instruction,
      onInstruction: (instruction) => { expertEntry.instruction = instruction; rerender(); },
      onWake: () => { void wakeWikipediaExpert(slug, rerender); },
      onAdvance: (output, nextState) => { void advanceDraft(slug, output, nextState, rerender); },
      onVerify: (output) => {
        void verifyDraftViaProbe(slug, output, rerender);
      },
    }); break;
    case 'cites': panel = connectorEvidenceWithFindings(connectorCitationsPanel(conn), outputs); break;
    case 'edits': panel = connectorEvidenceWithFindings(connectorEditsPanel(conn), outputs); break;
    default: panel = connectorEvidenceWithFindings(connectorContentPanel(conn), outputs);
  }
  return html`<div class="wikiwork">
    ${surfaceContextBar(brand, slug, 'wiki')}
    <div class="ww-content">
      <header class="ww-titlebar">
        <span class="ww-eyebrow">Managed reputation surface · ${brand}</span>
        <h1 class="ww-h1">Wikipedia working hub</h1>
        <p class="ww-lede">Review the live narrative, prepare policy-compliant requests, and verify what actually lands.
          Five Blocks advises and drafts; people decide and submit.</p>
        <span class="ww-service-chip">Five Blocks service line</span>
      </header>
      ${healthCard(state, conn, outputs)}
      ${invariantBar}
      ${tabStrip(rerender)}
      ${panel}
      ${coverageFooter(state, conn)}
    </div>
  </div>`;
}
